import type {
  AccessCatalogItemPublic,
  AccessRecordPublic,
  ApplicationPublic,
  UserPublic,
} from '@accounts/shared';
import { type CSSProperties, type ReactNode, useState } from 'react';

import { STR } from '../../../../i18n';
import { eyebrowStyle } from '../../../../ui/styles';
import { itemLabel } from '../accessFormat';
import { type AppStatusIndex, appStatusAt } from '../appStatus';
import { rowShort } from '../catalog/hierarchy';

import { AppMatrixCell, MatrixCell } from './MatrixCell';
import {
  heatGrid,
  heatKey,
  heatShade,
  type HeatSquare,
  isOpenWork,
  type RecordIndex,
  squareEntries,
} from './model';

/** The default side list shows this much open work before "Show all". */
const WORK_PREVIEW = 12;

/**
 * The Matrix's third view (round-2 demo A3, "Heat grid and side list"): a compact grid of
 * top-level groups against the entries below them, both read normally, with a readable side
 * list for whichever square is picked. The Suite apps lead as their own strip, as they lead
 * the other two views. Everything is drawn from the same filtered people and catalog rows
 * as Row and Table view, so the toolbar's filters change it exactly as they change those.
 *
 * Shades are the accent mixed into the surface (tokens only), so every palette, light and
 * dark, follows. A record in the side list is the same cell as in the other views: it opens
 * the record and its history.
 */
export function MatrixHeatGrid({
  groups,
  apps,
  people,
  index,
  appIndex,
  quiet,
  topLevel,
  nextLevel,
  onOpen,
  onEmpty,
}: {
  groups: readonly (readonly [string, readonly AccessCatalogItemPublic[]])[];
  apps: readonly ApplicationPublic[];
  people: readonly UserPublic[];
  index: RecordIndex;
  appIndex: AppStatusIndex;
  /** "Only what needs work": squares with nothing open step back. */
  quiet: boolean;
  /** The first two hierarchy level names, for the side list's explanation. */
  topLevel: string | undefined;
  nextLevel: string | undefined;
  onOpen: (record: AccessRecordPublic) => void;
  onEmpty: (userId: string, catalogItemId: string) => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [allWork, setAllWork] = useState(false);
  const grid = heatGrid(groups, people, index);
  const personById = new Map(people.map((person) => [person.id, person]));

  const appTally = apps.map((application) => {
    let granted = 0;
    let asked = 0;
    for (const person of people) {
      const status = appStatusAt(appIndex, person.id, application.id);
      if (status === 'granted') granted += 1;
      else if (status === 'requested') asked += 1;
    }
    return { application, granted, asked };
  });
  const appMax = Math.max(1, ...appTally.map((tally) => tally.granted));

  const pickedSquare = picked === null ? undefined : grid.squares.get(picked);
  const pickedApp =
    picked === null ? undefined : appTally.find((tally) => appKey(tally.application.id) === picked);

  const toggle = (key: string) => {
    setPicked((current) => (current === key ? null : key));
  };

  const squareButton = (
    key: string,
    count: number,
    open: number,
    shade: number,
    title: string,
  ): ReactNode => {
    const on = picked === key;
    return (
      <button
        type="button"
        aria-pressed={on}
        title={title}
        aria-label={title}
        onClick={() => {
          toggle(key);
        }}
        style={{
          ...squareStyle,
          border: on ? '2px solid var(--accent)' : '1px solid transparent',
          background: `color-mix(in srgb, var(--accent) ${String(shade)}%, var(--surface))`,
        }}
      >
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
        {open > 0 ? <span style={dotStyle} aria-hidden="true" /> : null}
      </button>
    );
  };

  const cornerHead = <th style={{ width: 150 }} />;

  const appStrip =
    apps.length === 0 ? null : (
      <table style={gridTable(apps.length)} aria-label={STR.access.matrix.suiteApps}>
        <thead>
          <tr>
            {cornerHead}
            {apps.map((application) => (
              <th key={application.id} title={application.name} style={colHeadStyle}>
                {application.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" style={rowHeadStyle}>
              {STR.access.matrix.suiteApps}
            </th>
            {appTally.map(({ application, granted, asked }) => (
              <td key={application.id} style={{ padding: 3 }}>
                {squareButton(
                  appKey(application.id),
                  granted,
                  asked,
                  heatShade(granted, appMax, false),
                  STR.access.matrix.heatAppTitle(application.name, granted, asked),
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );

  const outsideGrid =
    grid.columns.length === 0 ? null : (
      <table style={gridTable(grid.columns.length)} aria-label={STR.access.matrix.heatLabel}>
        <thead>
          <tr>
            {cornerHead}
            {grid.columns.map((column) => (
              <th key={column} title={column} style={colHeadStyle}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.groups.map((group) => (
            <tr key={group}>
              <th scope="row" title={group} style={rowHeadStyle}>
                {group}
              </th>
              {grid.columns.map((column) => {
                const square = grid.squares.get(heatKey(group, column));
                if (square === undefined) {
                  return (
                    <td
                      key={column}
                      title={STR.access.matrix.heatNoRow}
                      style={{ background: 'var(--surface-2)', borderRadius: 8 }}
                    />
                  );
                }
                return (
                  <td key={column} style={{ padding: 3 }}>
                    {squareButton(
                      square.key,
                      square.people,
                      square.open,
                      heatShade(square.people, grid.max, quiet && !square.attention),
                      STR.access.matrix.heatSquareTitle(group, column, square.people, square.open),
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );

  let side: ReactNode;
  if (pickedSquare !== undefined) {
    side = (
      <SquareList
        square={pickedSquare}
        quiet={quiet}
        people={people}
        index={index}
        onOpen={onOpen}
        onEmpty={onEmpty}
        onClose={() => {
          setPicked(null);
        }}
      />
    );
  } else if (pickedApp !== undefined) {
    const listed = people
      .map((person) => ({ person, status: appStatusAt(appIndex, person.id, pickedApp.application.id) }))
      .filter(({ status }) => status === 'granted' || status === 'requested')
      .sort((a, b) => (a.status === b.status ? 0 : a.status === 'requested' ? -1 : 1));
    side = (
      <>
        <SideHead
          title={pickedApp.application.name}
          meta={STR.access.matrix.heatAppMeta(pickedApp.granted, pickedApp.asked)}
          onClose={() => {
            setPicked(null);
          }}
        />
        <ul style={{ display: 'flex', flexDirection: 'column' }}>
          {listed.length === 0 ? (
            <li className="tx-subtle" style={{ fontSize: 12.5, padding: '10px 0' }}>
              {STR.access.matrix.heatNobody}
            </li>
          ) : (
            listed.map(({ person, status }) => (
              <li key={person.id} style={sideRowStyle}>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 650 }}>{person.name}</span>
                <span style={{ flex: 'none' }}>
                  <AppMatrixCell person={person} application={pickedApp.application} status={status} />
                </span>
              </li>
            ))
          )}
        </ul>
      </>
    );
  } else {
    const work: { record: AccessRecordPublic; item: AccessCatalogItemPublic }[] = [];
    for (const square of grid.squares.values()) {
      for (const record of square.records) {
        const item = square.items.find((candidate) => candidate.id === record.catalogItem.id);
        if (item !== undefined && isOpenWork(record)) work.push({ record, item });
      }
    }
    const shown = allWork ? work : work.slice(0, WORK_PREVIEW);
    side = (
      <>
        <div style={{ fontSize: 15, fontWeight: 650 }}>{STR.access.matrix.heatPick}</div>
        <div className="tx-subtle" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
          {STR.access.matrix.heatPickBody(
            (topLevel ?? STR.access.matrix.heatTopFallback).toLowerCase(),
            (nextLevel ?? STR.access.matrix.heatNextFallback).toLowerCase(),
          )}
        </div>
        <div className="tx-subtle" style={{ ...eyebrowStyle, marginTop: 8 }}>
          {STR.access.matrix.heatOpenWork(work.length)}
        </div>
        {work.length === 0 ? (
          <div className="tx-subtle" style={{ fontSize: 12.5 }}>
            {STR.access.matrix.heatNothingOpen}
          </div>
        ) : (
          <ul style={{ display: 'flex', flexDirection: 'column' }}>
            {shown.map(({ record, item }) => {
              const person = personById.get(record.user.id);
              if (person === undefined) return null;
              return (
                <li key={record.id} style={sideRowStyle}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 650 }}>{person.name}</span>
                    <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5 }}>
                      {itemLabel(item.path, item.name)}
                    </span>
                  </span>
                  <MatrixCell
                    person={person}
                    item={item}
                    record={record}
                    quiet={false}
                    onOpen={onOpen}
                    onEmpty={onEmpty}
                  />
                </li>
              );
            })}
          </ul>
        )}
        {work.length > WORK_PREVIEW ? (
          <button
            type="button"
            className="tbtn"
            aria-expanded={allWork}
            onClick={() => {
              setAllWork((current) => !current);
            }}
            style={{ ...closeStyle, alignSelf: 'flex-start' }}
          >
            {allWork ? STR.access.matrix.heatShowFewer : STR.access.matrix.heatShowAll(work.length)}
          </button>
        ) : null}
      </>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) 340px',
        gap: 16,
        alignItems: 'start',
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0, overflowX: 'auto' }}>
        {appStrip}
        {outsideGrid}
      </div>
      <aside
        aria-live="polite"
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {side}
      </aside>
    </div>
  );
}

const appKey = (applicationId: string): string => `app:${applicationId}`;

function SideHead({ title, meta, onClose }: { title: string; meta: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 650 }}>{title}</div>
        <div className="tx-subtle" style={{ fontSize: 12 }}>
          {meta}
        </div>
      </div>
      <button type="button" className="tbtn" onClick={onClose} style={closeStyle}>
        {STR.access.matrix.heatClose}
      </button>
    </div>
  );
}

/**
 * A picked square (`squareEntries`): the same cells as Row and Table view, empty ones
 * included, so Add access… is reached the same way; "Only what needs work" steps active
 * cells back as it does there.
 */
export function SquareList({
  square,
  quiet,
  people,
  index,
  onOpen,
  onEmpty,
  onClose,
}: {
  square: HeatSquare;
  quiet: boolean;
  people: readonly UserPublic[];
  index: RecordIndex;
  onOpen: (record: AccessRecordPublic) => void;
  onEmpty: (userId: string, catalogItemId: string) => void;
  onClose: () => void;
}) {
  const several = square.items.length > 1;
  const listed = squareEntries(square, people, index);
  return (
    <>
      <SideHead
        title={`${square.group}${STR.accessCommon.pathSeparator}${square.column}`}
        meta={STR.access.matrix.heatSquareMeta(square.items.length, square.people)}
        onClose={onClose}
      />
      <ul style={{ display: 'flex', flexDirection: 'column' }}>
        {listed.length === 0 ? (
          <li className="tx-subtle" style={{ fontSize: 12.5, padding: '10px 0' }}>
            {STR.access.matrix.heatNobody}
          </li>
        ) : (
          listed.map(({ person, item, record }) => (
            <li key={`${person.id}|${item.id}`} style={sideRowStyle}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 650 }}>{person.name}</span>
                {several ? (
                  <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5 }}>
                    {rowShort(item)}
                  </span>
                ) : null}
              </span>
              <MatrixCell
                person={person}
                item={item}
                record={record}
                quiet={quiet}
                onOpen={onOpen}
                onEmpty={onEmpty}
              />
            </li>
          ))
        )}
      </ul>
    </>
  );
}

/** Fixed layout keeps every square one width; the floor keeps a wide grid scrollable. */
const gridTable = (columns: number): CSSProperties => ({
  borderCollapse: 'separate',
  borderSpacing: 0,
  width: '100%',
  minWidth: 150 + columns * 68,
  tableLayout: 'fixed',
});

const colHeadStyle: CSSProperties = {
  padding: '0 2px 6px',
  fontSize: 11,
  fontWeight: 650,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const rowHeadStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 650,
  paddingRight: 10,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const squareStyle: CSSProperties = {
  width: '100%',
  height: 40,
  borderRadius: 8,
  color: 'var(--text)',
  fontSize: 13,
  fontWeight: 650,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  cursor: 'pointer',
};

const dotStyle: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 999,
  background: 'var(--bg-warn)',
  boxShadow: '0 0 0 2px var(--surface)',
};

const closeStyle: CSSProperties = {
  padding: '4px 9px',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 650,
  cursor: 'pointer',
};

const sideRowStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  padding: '6px 0',
  borderBottom: '1px solid var(--border)',
  fontSize: 12.5,
};
